/*
 * QuoteLineForm.tsx : Un formulaire pour ajouter ou modifier une ligne sur un devis.
 */

// Import React
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	InputField,
	SelectField,
	TextAreaField,
	ToggleButtonField,
	finishFormSave,
	FormSaveActions,
	useFormDirty,
} from '../../../common';

// Import Devis
import { QuoteLineData } from '../types/types';
import { quoteLineApi } from '../services/quoteLineApi';
import { quotesVersionApi } from '../services/quoteVersionApi';

// Import Taxes, produits et services
import { ProductData, productsApi, TaxRateData } from 'features/products';
import { taxRatesApi } from '../../taxRate/services/taxRateApi';
import { ServiceData, servicesApi } from 'features/services';

// Composant QuoteForm
const QuoteLineForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<QuoteLineData>({
		id: 0,
		description: '',
		unitPrice: 0.0,
		quantity: 1.0,
		total: 0.0,
		nbDays: 0,
		nbParticipants: 0,
		manualOverride: false,
		service: undefined,
		product: undefined,
		taxRate: {
			code: '',
			label: '',
			rate: 0,
			country: '',
			isActive: true,
		},
		quoteVersion: {
			id: 0,
			label: '',
			versionIndex: 0,
			quoteLines: [],
			quote: { id: 0, referenceNumber: '', budgeted: false, quoteVersions: [] },
		},
	});

	// Hook de state pour la liste des taux de taxes, des produits et des services
	const [taxRates, setTaxRates] = useState<TaxRateData[]>([]);
	const [products, setProducts] = useState<ProductData[]>([]);
	const [services, setServices] = useState<ServiceData[]>([]);

	// Options pour les selects correspondants
	const TaxRateOptions = taxRates.map((taxRate) => ({
		key: taxRate.code,
		label: taxRate.label || '',
	}));
	const ProductOptions = products.map((product) => ({
		key: product.id.toString(),
		label: product.label || '',
	}));
	const ServiceOptions = services.map((service) => ({
		key: service.id.toString(),
		label: service.label || '',
	}));

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch du service et préremplissage du formulaire
	React.useEffect(() => {
		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Dans tous les cas, on charge la liste des taux de taxes, des produits et des services
		taxRatesApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setTaxRates(res.data))
			.catch((error) =>
				console.error('Erreur lors du chargement des taux de taxe :', error)
			);

		productsApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setProducts(res.data))
			.catch((error) =>
				console.error('Erreur lors du chargement des produits :', error)
			);

		servicesApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setServices(res.data))
			.catch((error) =>
				console.error('Erreur lors du chargement des services :', error)
			);

		// Si un id est présent, on fetch la version du devis
		if (id) {
			quoteLineApi
				.get(Number(id), token)
				.then((data: QuoteLineData) => {
					setFormData(data);
					setLoading(false);
				})
				.catch((error) => {
					console.error(
						'Erreur lors du chargement de la ligne du devis :',
						error
					);
					setLoading(false);
				})
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Sinon, si on vérifie la transmission de l'id de la version du devis dans l'URL
			const urlParams = new URLSearchParams(window.location.search);
			const quoteVersionId = urlParams.get('quoteVersionId');
			if (quoteVersionId) {
				quotesVersionApi
					.get(Number(quoteVersionId), token)
					.then((data) => {
						setFormData((prev) => ({
							...prev,
							quoteVersion: data,
						}));
					})
					.catch((error) => {
						console.error(
							'Erreur lors du chargement de la version du devis :',
							error
						);
					});
			}
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	React.useEffect(() => {
		if (!id && taxRates.length > 0) {
			setFormData((prev) => {
				// si prev n'existe pas (sécurité) ou si prev.taxRate.code est vide, on applique la présélection
				const needsDefault =
					!prev ||
					!prev.taxRate ||
					!prev.taxRate.code ||
					prev.taxRate.code.trim() === '';

				if (!needsDefault) return prev;

				const defaultTax = taxRates.find((tr) => tr.code === 'TVA_FR_20');
				return {
					...prev,
					taxRate: defaultTax ?? prev.taxRate,
				};
			});
		}
	}, [taxRates, id]);

	// Gestion des changements dans le formulaire
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		const newFormData = { ...formData, [name]: value };

		// Gestion de la modification du nombre de jours ou de participants
		const nbDays = name === 'nbDays' ? Number(value) : formData.nbDays || 0;
		const nbParticipants =
			name === 'nbParticipants' ? Number(value) : formData.nbParticipants || 0;
		if (
			!formData.manualOverride &&
			(name === 'nbDays' || name === 'nbParticipants')
		) {
			newFormData.quantity = nbDays * nbParticipants;
			newFormData.total = newFormData.unitPrice * newFormData.quantity;
		}

		// Gestion de la modification du prix unitaire ou de la quantité
		if (name === 'unitPrice' || name === 'quantity') {
			newFormData.total = newFormData.unitPrice * newFormData.quantity;
		}

		setFormData(newFormData);
	};

	const handleToggleManualOverride = (value: boolean) => {
		setFormData((prev) => {
			const newFormData = { ...prev, manualOverride: value };
			// Si on désactive la saisie manuelle, recalculer la quantité et le total automatiquement
			if (!value) {
				const nbDays = prev.nbDays || 0;
				const nbParticipants = prev.nbParticipants || 0;
				newFormData.quantity = nbDays * nbParticipants;
				newFormData.total = newFormData.unitPrice * newFormData.quantity;
			}
			return newFormData;
		});
	};

	const handleToggleSubcontracted = (value: boolean) => {
		setFormData((prev) => {
			const newFormData = { ...prev, subcontracted: value };
			return newFormData;
		});
	};

	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Si name est taxRate, on doit mettre à jour l'objet complet
		if (name === 'taxRate') {
			const selectedOption = TaxRateOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				const taxRate = taxRates.find((tr) => tr.code === selectedOption.key);
				setFormData((prev) => ({
					...prev,
					taxRate: taxRate || prev.taxRate,
				}));
			}
			return;
		} else {
			// Si name est service ou product, on doit aussi mettre à jour l'objet complet
			if (name === 'service') {
				const selectedOption = ServiceOptions.find((opt) => opt.key === value);
				if (selectedOption) {
					const serviceId = Number(selectedOption.key);
					const service = services.find((s) => s.id === serviceId);
					setFormData((prev) => ({
						...prev,
						service: service,
						product: undefined,
						description: service ? service.label || '' : prev.description,
						unitPrice: service ? service.defaultPrice || 0.0 : prev.unitPrice,
						taxRate: service
							? service.defaultTaxRate || prev.taxRate
							: prev.taxRate,
						total:
							(service ? service.defaultPrice || 0.0 : prev.unitPrice) *
							prev.quantity,
					}));
				}
				return;
			} else if (name === 'product') {
				const selectedOption = ProductOptions.find((opt) => opt.key === value);
				if (selectedOption) {
					const productId = Number(selectedOption.key);
					const product = products.find((p) => p.id === productId);
					setFormData((prev) => ({
						...prev,
						product: product,
						service: undefined,
						description: product ? product.label || '' : formData.description,
						unitPrice: product
							? product.defaultPrice || 0.0
							: formData.unitPrice,
						taxRate: product
							? product.defaultTaxRate || formData.taxRate
							: formData.taxRate,
						total:
							(product ? product.defaultPrice || 0.0 : formData.unitPrice) *
							formData.quantity,
					}));
				}
				return;
			}
			// Pour les autres champs, mise à jour standard
			setFormData((prev) => ({ ...prev, [name]: value }));
		}
	};
	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const redirectPath =
			`/app/quoteVersions/${formData.quoteVersion?.id}/manage` || '/app/quotes';
		// Si un id est présent, mise à jour du devis, puis on navigue vers la page de la dernière version du devis
		if (id) {
			quoteLineApi
				.update(Number(id), formData, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave: () =>
							navigate(`/app/quoteVersions/${formData.quoteVersion.id}/manage`),
						markClean,
					})
				)
				.catch((error) =>
					console.error(
						'Erreur lors de la mise à jour de la ligne du devis :',
						error
					)
				)
				.finally(() => setIsSubmitting(false));
			// Sinon, création d'une nouvelle ligne de devis
		} else {
			quoteLineApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate(redirectPath),
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/quoteLines/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) =>
					console.error(
						'Erreur lors de la création de la ligne du devis :',
						error
					)
				)
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		handleSave(true);
	};

	// Rendu du composant
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faFileContract}
						className="w-20 h-20 text-green-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id ? "Édition d'une ligne de devis" : 'Nouvelle ligne de devis'}
						</h1>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 3 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Devis"
									name="referenceNumber"
									value={formData.quoteVersion?.quote?.referenceNumber || ''}
									onChange={handleChange}
									loading={loading}
									placeholder=""
									readOnly={true}
								/>
								<InputField
									label="Version de devis"
									name="quoteVersion"
									value={
										formData.quoteVersion?.label +
											'' +
											' (indice ' +
											formData.quoteVersion?.versionIndex +
											')' || ''
									}
									onChange={handleChange}
									required
									loading={loading}
									placeholder=""
									readOnly={true}
								/>
								<SelectField
									label="Service"
									selectedKey={formData.service?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'service',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={ServiceOptions}
									loading={loading}
									entityHref={
										formData.service?.id
											? `/app/settings/services/${formData.service.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											service: null,
										}))
									}
								/>
								<SelectField
									label="Produit"
									selectedKey={formData.product?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'product',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={ProductOptions}
									loading={loading}
									entityHref={
										formData.product?.id
											? `/app/settings/products/${formData.product.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											product: null,
										}))
									}
								/>
								<SelectField
									label="Taxe"
									selectedKey={formData.taxRate.code}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'taxRate',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={TaxRateOptions}
									entityHref={
										formData.taxRate?.id
											? `/app/taxrates/${formData.taxRate.id}/edit`
											: undefined
									}
								/>
							</div>

							{/* COLONNE CENTRE */}
							<div className="space-y-4">
								<InputField
									label="Prix unitaire (HT)"
									name="unitPrice"
									value={formData.unitPrice.toString()}
									onChange={handleChange}
									loading={loading}
									placeholder="ex : 1000.00"
								/>
								<InputField
									label="Total (HT)"
									name="total"
									value={formData.total.toString()}
									onChange={handleChange}
									loading={loading}
									placeholder="ex : 1000.00"
									readOnly={true}
								/>
								<InputField
									label="Total (TTC)"
									name="totalttc"
									value={(
										formData.total *
										(1 + formData.taxRate.rate)
									).toString()}
									onChange={handleChange}
									loading={loading}
									placeholder="calculé automatiquement"
									readOnly={true}
								/>
								<TextAreaField
									label="Description"
									name="description"
									value={formData.description}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : XXX"
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Nombre de jours"
									name="nbDays"
									value={formData.nbDays?.toString()}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 5"
								/>
								<InputField
									label="Nombre de participants"
									name="nbParticipants"
									value={formData.nbParticipants?.toString()}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 10"
								/>
								<InputField
									label="Quantité"
									name="quantity"
									value={formData.quantity.toString()}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 1.0"
									readOnly={!formData.manualOverride}
								/>
								<ToggleButtonField
									label="Saisie manuelle"
									isOn={formData.manualOverride ? true : false}
									onChange={handleToggleManualOverride}
									invert={true}
									size="sm"
									loading={loading}
								/>
								<ToggleButtonField
									label="Sous-traité"
									isOn={formData.subcontracted ? true : false}
									onChange={handleToggleSubcontracted}
									invert={true}
									size="sm"
									loading={loading}
								/>
							</div>
						</div>
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => {
							if (id) {
								navigate(
									`/app/quoteVersions/${formData.quoteVersion.id}/manage`
								);
							} else {
								navigate(
									`/app/quoteVersions/${formData.quoteVersion?.id}/manage` ||
										'/app/quotes'
								);
							}
						}}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default QuoteLineForm;
